/**
 * POC animated lobby — real jackpot-hit trigger.
 * Watches the jackpots store: when a level's API target drops to less than half of its previous
 * target (a win resets the level to its seed), the celebration plays with the value it had before
 * the drop. Heuristic, throttled to one celebration per 15 s. Started by App.tsx under ?fx=1 only.
 */

import { formatCurrencyValue } from '@repo/common';
import { effectsBus, FX_ENABLED } from 'shared/effects';
import { useJackpotsStore } from '../store/jackpotsStoreSimple';

const WIN_DROP_RATIO = 0.5;
const COOLDOWN_MS = 15_000;

let lastFiredAt = 0;
let unsubscribe: (() => void) | null = null;

export const startJackpotWinWatcher = (): (() => void) => {
  if (!FX_ENABLED || unsubscribe) return () => undefined;

  unsubscribe = useJackpotsStore.subscribe((state, previous) => {
    if (state.animatedValues === previous.animatedValues || previous.animatedValues.size === 0) return;
    const now = Date.now();
    if (now - lastFiredAt < COOLDOWN_MS) return;

    let fired = false;
    state.animatedValues.forEach((next, key) => {
      if (fired) return;
      const before = previous.animatedValues.get(key);
      if (!before || before.targetValue <= 0 || next.targetValue >= before.targetValue * WIN_DROP_RATIO) return;

      fired = true;
      lastFiredAt = now;
      const currency = state.jackpotsFromAPI.get(next.jackpotName)?.currency ?? '';
      const amount = `${formatCurrencyValue(Math.round(before.targetValue), 100)} ${currency}`.trim();
      effectsBus.playCelebration('jackpotHit', { amount, label: 'fx_jackpot_hit' });
    });
  });

  return () => {
    unsubscribe?.();
    unsubscribe = null;
  };
};
